import { useState } from 'react';
import { PartyPopper, Star } from 'lucide-react';
import { GlassModal } from '@/components/ui/glass-modal';
import { Button } from '@/components/ui/button';
import { LeaveReviewDialog } from '@/components/social/LeaveReviewDialog';
import { toast } from '@/lib/toast';
import { goldGradientActive } from '@/lib/gradientStyles';
import { cn } from '@/lib/utils';

interface SaleReviewPromptProps {
  open: boolean;
  transactionId: string;
  buyerId: string;
  buyerName: string;
  /** Fires when the prompt (and any nested review dialog) is fully dismissed. */
  onDismiss: () => void;
}

/**
 * Post-sale "leave a review" prompt shown when a sale was linked to a known buyer.
 * Shared between MarkAsSoldDialog (+ menu path) and QuickPriceEditDialog (edit modal path)
 * so both surfaces surface the same review flow.
 */
export function SaleReviewPrompt({
  open,
  transactionId,
  buyerId,
  buyerName,
  onDismiss,
}: SaleReviewPromptProps) {
  const [showReviewDialog, setShowReviewDialog] = useState(false);

  const handleSkip = () => {
    toast.success('Item marked as sold!', {
      description: 'You can leave a review later from your transaction history.',
    });
    onDismiss();
  };

  const handleReviewComplete = () => {
    setShowReviewDialog(false);
    onDismiss();
  };

  if (showReviewDialog) {
    return (
      <LeaveReviewDialog
        open={showReviewDialog}
        onOpenChange={(o) => {
          if (!o) handleReviewComplete();
        }}
        transactionId={transactionId}
        reviewedUserId={buyerId}
        reviewedUserName={buyerName}
      />
    );
  }

  return (
    <GlassModal
      open={open}
      onOpenChange={(o) => {
        if (!o) handleSkip();
      }}
      title="Congratulations on your sale!"
      description={`Would you like to leave a review for ${buyerName}?`}
    >
      <div className="space-y-2 text-left">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <PartyPopper className="w-5 h-5 text-caution" />
          Congratulations on your sale!
        </h2>
        <p className="text-sm text-muted-foreground">
          Would you like to leave a review for {buyerName}?
        </p>
      </div>

      <div className="py-6 text-center space-y-4">
        <div className="flex justify-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star key={star} className="w-8 h-8 text-active-gold fill-active-gold" />
          ))}
        </div>
        <p className="text-sm text-muted-foreground">
          Reviews help build trust in our marketplace and help other sellers know who to trade with.
        </p>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={handleSkip} className="flex-1">
          Maybe Later
        </Button>
        <Button
          onClick={() => setShowReviewDialog(true)}
          className={cn('flex-1', goldGradientActive)}
        >
          <Star className="w-4 h-4 mr-2" />
          Leave Review
        </Button>
      </div>
    </GlassModal>
  );
}
